
import React, { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LoginForm from "@/components/auth/login/LoginForm";
import RegisterForm from "@/components/auth/register/RegisterForm";
import AuthHero from "@/components/auth/AuthHero";
import AuthImage from "@/components/auth/AuthImage";
import UserTypeSelection from "@/components/auth/user-type/UserTypeSelection";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { useAuth } from "@/components/auth/AuthProvider";
import { Helmet } from "react-helmet";

export default function Auth() {
  const navigate = useNavigate();
  const { action } = useParams();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirectTo');
  const userType = searchParams.get('userType');

  // Redirect authenticated users unless they need to complete a specific flow
  useEffect(() => {
    if (user) {
      // Ensure redirectTo always has a leading slash when navigating
      if (redirectTo) {
        const path = redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;
        navigate(path);
      } else {
        // Default redirect to dashboard
        navigate("/dashboard");
      }
    }
  }, [user, navigate, redirectTo]);

  // If user is already authenticated, don't render the form (redirect will happen via useEffect)
  if (user) {
    return (
      <Layout hideFooter className="relative min-h-screen flex items-center justify-center bg-grandpal-beige/30">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-grandpal-green mb-4" />
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </Layout>
    );
  }

  const defaultTab = action === "signup" ? "register" : "login";
  
  const handleTabChange = (tab: string) => {
    const currentSearchParams = new URLSearchParams(location.search);
    const path = tab === "register" ? "/auth/signup" : "/auth/login";
    
    navigate(`${path}${currentSearchParams.toString() ? '?' + currentSearchParams.toString() : ''}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <Layout hideFooter className="relative min-h-screen flex flex-col items-center justify-center bg-grandpal-beige/30">
      <Helmet>
        <title>{action === "signup" ? "Create Account" : "Sign In"} | Grandpal</title>
      </Helmet>
      
      <div className="container relative z-10 flex flex-col items-center justify-center px-4 py-8 max-w-5xl">
        <motion.div 
          className="w-full flex flex-col items-center text-center mb-8"
          initial="hidden"
          animate="show"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <AuthHero />
          </motion.div>
        </motion.div>
        
        {!userType ? (
          <motion.div 
            className="w-full max-w-2xl mb-12"
            initial="hidden"
            animate="show"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <UserTypeSelection />
            </motion.div>
          </motion.div>
        ) : (
          <motion.div 
            className="w-full max-w-md mb-12"
            initial="hidden"
            animate="show"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <Card className="w-full bg-white p-8 rounded-xl shadow-lg border border-gray-100">
                <Tabs defaultValue={defaultTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8">
                    <TabsTrigger 
                      value="login" 
                      onClick={() => handleTabChange("login")}
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger 
                      value="register" 
                      onClick={() => handleTabChange("register")}
                    >
                      Create Account
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="login">
                    <LoginForm />
                  </TabsContent>
                  
                  <TabsContent value="register">
                    <RegisterForm />
                  </TabsContent>
                </Tabs>
              </Card>
            </motion.div>
          </motion.div>
        )}

        <motion.div 
          className="w-full flex justify-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <AuthImage />
        </motion.div>
      </div>
    </Layout>
  );
}
